'use client';

import { useTranslation } from 'next-i18next';
import { Anchor, Avatar } from '@components/common';
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/solid';
import Link from 'next/link';
import Slider from 'react-slick';
import { OfferPageInfobox } from '@components/templates/employer/offer/OfferPageInfobox';

interface Company {
  slug: string;
  name: string;
  logo: string;
}

export const OfferPageStandaloneSection = () => {
  const { t } = useTranslation('common');

  /** Companies */
  const companies: Company[] = [
    { slug: 'vodafone', name: 'VODAFONE ROMANIA SA', logo: '6272a2e603c4e.jpg' },
    { slug: 'orange', name: 'Orange Romania SA', logo: '58318abb20e40.jpg' },
    { slug: 'telekom', name: 'TELEKOM ROMANIA', logo: '5a3a5589584b3.jpg' },
    { slug: 'oracle', name: 'Oracle Romania', logo: '5d442dbb87c0e.png' },
    { slug: 'dante-international', name: 'DANTE INTERNATIONAL SA', logo: '60a4aea5760c4.png' },
    { slug: 'kaufland', name: 'Kaufland Romania', logo: '58f614495621d.jpg' },
    { slug: 'carrefour', name: 'Carrefour România', logo: '58318b10341d5.jpg' },
    { slug: 'mega-image', name: 'Mega Image', logo: '5f6dc9f1338b1.jpg' },
    { slug: 'auchan', name: 'AUCHAN ROMÂNIA SA', logo: '58318adb35232.jpg' },
    { slug: 'altex', name: 'Altex Romania SRL', logo: '58318a7765651.jpg' },
    { slug: 'raiffeisen-bank', name: 'Raiffeisen Bank S.A.', logo: '5a2814ea36415.jpg' },
    { slug: 'banca-transilvania', name: 'Banca Transilvania', logo: '5984213624fa7.PNG' },
    { slug: 'brd-groupe-societe-generale', name: 'BRD - Groupe Societe Generale', logo: '58318ad4a0a5d.jpg' },
    { slug: 'alpha-bank', name: 'ALPHA BANK ROMANIA', logo: '673643724eab3.png' },
  ];

  /** Slider settings */
  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 5000,
    slidesToShow: 7,
    slidesToScroll: 4,
    initialSlide: 0,
    autoplay: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
          infinite: true,
          dots: true,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          initialSlide: 3,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
    ],
  };

  return (
    <div className="bg-surface px-5 py-10 shadow-lg mb-10">
      {/* Infobox */}
      <OfferPageInfobox />

      {/* Companies Slider */}
      <div className="rounded-md bg-white p-3">
        <Slider {...settings}>
          {companies.map((company) => (
            <div
              key={company.slug}
              className="!flex h-20 w-full items-center justify-center p-2"
            >
              <Link href={`/company-profile/${company.slug}`} target="_blank">
                <Avatar
                  imgPath={company.logo}
                  name={company.name}
                  imgFolder="employer_logo"
                  extractImageName
                  width={150}
                  aspectRatio="landscape"
                  className="max-h-full !max-w-[150px] object-contain grayscale"
                />
              </Link>
            </div>
          ))}
        </Slider>
      </div>

      {/* Contact Section */}
      <div className="my-5 flex items-center justify-center gap-5">
        <Anchor
          rounding="full"
          styling="none"
          color="primary"
          href={`tel:${t('global.support.phoneNr')}`}
          className="mr-2 flex items-center justify-center gap-1 text-primary"
        >
          <PhoneIcon className="mr-2 w-4" />
          {t('global.support.phoneNr')}
        </Anchor>

        <Anchor
          rounding="full"
          styling="none"
          color="primary"
          href={`mailto:${t('global.support.email')}`}
          className="mr-2 flex items-center justify-center gap-1 text-primary"
        >
          <EnvelopeIcon className="mr-2 w-4" />
          {t('global.support.email')}
        </Anchor>
      </div>
    </div>
  );
};
